import * as ast from 'notebook/tables/excess/AST';
import * as util from 'notebook/tables/excess/Util';

export interface FunctionCostConfig {
  baseCost: number;
  perArgCost: number;
}

export enum CostBreakdownKey {
  CELL_COUNT = 'Cell Count',
}
export interface CostBreakdown {
  [key: string | CostBreakdownKey]: number;
}

export interface FunctionCostRegistry {
  [functionName: string]: FunctionCostConfig;
}

export const DEFAULT_FUNCTION_COST_REGISTRY: FunctionCostRegistry = {
  DEFAULT: {baseCost: 5, perArgCost: 2},
};

export interface CellGrids {
  [documentName: string]: {[tableName: string]: Array<Array<{text: string}>>};
}

export interface Constraints {
  [documentName: string]: DocumentConstraints;
}
interface DocumentConstraints {
  [tableName: string]: TableConstraints;
}

interface TableConstraints {
  maxRowIndex: number;
  maxColumnIndex: number;
}

const PARSE_ERROR_COST = 1;

function _getConstraints(
  documentName: string,
  tableName: string,
  constraints: Constraints
): TableConstraints {
  if (constraints[documentName] && constraints[documentName][tableName]) {
    return constraints[documentName][tableName];
  }
  return {
    maxRowIndex: Number.MAX_SAFE_INTEGER,
    maxColumnIndex: Number.MAX_SAFE_INTEGER,
  };
}

function _computeNodeCost(
  node: ast.ASTNode,
  costConfig: FunctionCostRegistry,
  costBreakdown: {[key: string]: number},
  /** The name of the document that the original node belongs to.  This is primarily used in CSDR scenarios. */
  documentName: string,
  /** The name of the table that the original node belongs to. */
  tableName: string,
  constraints: Constraints
): number {
  if (node instanceof ast.Value) {
    return 1;
  }
  if (node instanceof ast.ReferenceErrorNode) {
    return 1;
  }
  if (node instanceof ast.Cell || node instanceof ast.RelCell) {
    return 1;
  }
  if (node instanceof ast.CellRange) {
    const documentThatHostsCellRange = node.document || documentName;
    const tableThatHostsCellRange = node.table || tableName;
    const tableConstraints = _getConstraints(
      documentThatHostsCellRange,
      tableThatHostsCellRange,
      constraints
    );

    // Sometimes users put in a really large range (like A1:A10000000) that exceeds the actual number of rows or columns in the table.
    // We don't want to artificially inflate their complexity score for this, so we cap the max row/column
    // at the number of rows/columns in the table.
    const {start: trimmedStartRow, end: trimmedEndRow} = util.trimDimensionRange(
      node.start.row,
      node.end.row,
      tableConstraints.maxRowIndex
    );
    const {start: trimmedStartColumn, end: trimmedEndColumn} = util.trimDimensionRange(
      node.start.column,
      node.end.column,
      tableConstraints.maxColumnIndex
    );

    const numRows = Math.abs(trimmedEndRow - trimmedStartRow) + 1;
    const numColumns = Math.abs(trimmedEndColumn - trimmedStartColumn) + 1;
    return 1 + numRows * numColumns;
  }
  if (node instanceof ast.RelCellRange) {
    return 1;
  }
  if (node instanceof ast.UnaryOperator) {
    return 1 + _computeNodeCost(node.child, costConfig, costBreakdown, documentName, tableName, constraints);
  }
  if (node instanceof ast.BinaryOperator) {
    const leftCost = node.left
      ? _computeNodeCost(node.left, costConfig, costBreakdown, documentName, tableName, constraints)
      : 0;
    const rightCost = node.right
      ? _computeNodeCost(node.right, costConfig, costBreakdown, documentName, tableName, constraints)
      : 0;
    const name = node.type.toString();
    costBreakdown[name] = (costBreakdown[name] ?? 0) + 1;
    return 1 + leftCost + rightCost;
  }
  if (node instanceof ast.FunctionOperator) {
    const config = costConfig[node.type] ?? costConfig.DEFAULT ?? DEFAULT_FUNCTION_COST_REGISTRY.DEFAULT;
    const selfCost = config.baseCost + config.perArgCost * node.children.length;
    const name = node.type.toString();
    costBreakdown[name] = (costBreakdown[name] ?? 0) + selfCost;

    const childrenCost = node.children.reduce(
      (sum, child) =>
        sum + _computeNodeCost(child, costConfig, costBreakdown, documentName, tableName, constraints),
      0
    );
    return selfCost + childrenCost;
  }
  return 1;
}

export function computeConstraints(cellGrids: CellGrids): Constraints {
  const constraints: Constraints = {};
  for (const [docName, documentCellGrids] of Object.entries(cellGrids)) {
    constraints[docName] = {};
    for (const [tblName, grid] of Object.entries(documentCellGrids)) {
      const columnCount = grid[0]?.length ?? 0;
      constraints[docName][tblName] = {
        maxRowIndex: grid.length > 0 ? grid.length - 1 : 0,
        maxColumnIndex: columnCount > 0 ? columnCount - 1 : 0,
      };
    }
  }
  return constraints;
}

export function computeCellGridCost(
  cellGrid: Array<Array<{text: string}>>,
  constraints: Constraints,
  documentName: string,
  tableName: string,
  parse: (text: string) => ast.ASTNode,
  costConfig: FunctionCostRegistry = DEFAULT_FUNCTION_COST_REGISTRY
): {totalCost: number; costBreakdown: {[key: string]: number}} {
  let totalCost = 0;
  const rowCount = cellGrid.length;
  const columnCount = cellGrid[0]?.length ?? 0;
  const costBreakdown: {[key: string]: number} = {
    [CostBreakdownKey.CELL_COUNT]: rowCount === 0 ? 0 : rowCount * columnCount,
  };
  for (const row of cellGrid) {
    for (const cell of row) {
      try {
        const astNode = parse(cell.text);
        totalCost += _computeNodeCost(
          astNode,
          costConfig,
          costBreakdown,
          documentName,
          tableName,
          constraints
        );

        // parse errors are not a legitimate error in this case, since the user can legally
        // enter unparseable text, and we still want the cost analysis to continue if they do.
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch {
        totalCost += PARSE_ERROR_COST;
      }
    }
  }
  return {totalCost, costBreakdown};
}

export const _forTesting = {
  computeNodeCost: _computeNodeCost,
  getConstraints: _getConstraints,
};
